import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ApplicationInsightsService } from './application-insights.service';
import { UrlNormalizer } from '../utils/url-normalizer';

@Injectable({
  providedIn: 'root'
})
export class ApplicationInsightsRouterService {
  private readonly router = inject(Router);
  private readonly appInsights = inject(ApplicationInsightsService);
  private initialized = false;

  initialize(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe((event: NavigationEnd) => {
        this.trackPageView(event.urlAfterRedirects);
      });
  }

  private trackPageView(url: string): void {
    const properties = {
      url: url,
      timestamp: new Date().toISOString()
    };

    this.appInsights.trackPageView(
      this.getPageName(url),
      url,
      properties
    );
  }

  /**
   * Generate a human-readable page name from URL with normalized ID parameters
   *
   * Examples:
   *   /shipments/list -> "Shipments / List"
   *   /shipments/123 -> "Shipments / {id}"
   *   /shipments/405/details -> "Shipments / {id} / Details"
   *   /admin/users/abc-123/edit -> "Admin / Users / {guid} / Edit"
   */
  private getPageName(url: string): string {
    // Normalize URL first (replace IDs with placeholders)
    let pageName = UrlNormalizer.normalize(url);

    // Remove leading slash
    if (pageName.startsWith('/')) {
      pageName = pageName.substring(1);
    }

    // Handle root/home page
    if (pageName === '') {
      return 'Home';
    }

    // Capitalize each segment and join with " / "
    return pageName
      .split('/')
      .map(part => this.capitalizeSegment(part))
      .join(' / ');
  }

  /**
   * Capitalize a URL segment, preserving placeholders like {id}, {guid}
   */
  private capitalizeSegment(segment: string): string {
    // Keep placeholders as-is (lowercase)
    if (segment.startsWith('{') && segment.endsWith('}')) {
      return segment;
    }

    return segment.charAt(0).toUpperCase() + segment.slice(1);
  }
}
